import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
    Activity,
    AlertTriangle,
    BarChart3,
    Download,
    Eye,
    Filter,
    Image as ImageIcon,
    RefreshCw,
    Search,
    Shield,
    TrendingUp,
    Users,
    Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Circle, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import { mapAPI } from '../utils/api'
import { formatConfidence, formatDate, getStatusColor } from '../utils/helpers'

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

function GeoMap() {
  const [mapData, setMapData] = useState([])
  const [hotspots, setHotspots] = useState([])
  const [statistics, setStatistics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeView, setActiveView] = useState('activities') // 'activities', 'hotspots', 'heatmap'
  const [filters, setFilters] = useState({
    activity_type: '',
    confidence_threshold: 0.5,
    limit: 1000
  })
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [trendingManipulations, setTrendingManipulations] = useState([])
  const [highRiskUsers, setHighRiskUsers] = useState([])
  const [spikeAlerts, setSpikeAlerts] = useState([])
  const [aiMisinfoHotspots, setAiMisinfoHotspots] = useState([])
  const [suspiciousClusters, setSuspiciousClusters] = useState([])
  const [deepfakeRegions, setDeepfakeRegions] = useState([])
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false)

  useEffect(() => {
    loadMapData()
    loadStatistics()
    loadAdvancedAnalytics()
  }, [filters])

  const loadMapData = async () => {
    try {
      setLoading(true)
      const response = await mapAPI.getData(filters)
      setMapData(response.data.map_data)
    } catch (error) {
      console.error('Error loading map data:', error)
      // Use demo data when backend is not available
      const demoMapData = [
        {
          id: 1,
          lat: -17.8252,
          lng: 31.0335,
          activity_type: 'Document Forgery',
          confidence: 0.92,
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          description: 'Suspected forged national ID detected',
          user_info: { full_name: 'John Doe', username: 'jdoe' },
          document_info: { type: 'National ID', number: 'ZW123456' }
        },
        {
          id: 2,
          lat: -18.9645,
          lng: 32.7700,
          activity_type: 'Biometric Mismatch',
          confidence: 0.88,
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          description: 'Face verification failed - biometric mismatch',
          user_info: { full_name: 'Jane Smith', username: 'jsmith' },
          document_info: { type: 'Passport', number: 'ZW987654' }
        },
        {
          id: 3,
          lat: -20.1393,
          lng: 28.5833,
          activity_type: 'Document Forgery',
          confidence: 0.95,
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          description: 'High confidence forgery detected - security features missing',
          user_info: { full_name: 'Mike Johnson', username: 'mjohnson' },
          document_info: { type: 'Drivers License', number: 'ZW456789' }
        },
        {
          id: 4,
          lat: -19.4500,
          lng: 29.8167,
          activity_type: 'Suspicious Pattern',
          confidence: 0.78,
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          description: 'Multiple verification attempts from same location',
          user_info: { full_name: 'Sarah Williams', username: 'swilliams' },
          document_info: { type: 'National ID', number: 'ZW321654' }
        },
        {
          id: 5,
          lat: -17.8639,
          lng: 30.8889,
          activity_type: 'Low Confidence Verification',
          confidence: 0.65,
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          description: 'Document quality poor - manual review required',
          user_info: { full_name: 'David Brown', username: 'dbrown' },
          document_info: { type: 'Birth Certificate', number: 'ZW789123' }
        },
        {
          id: 6,
          lat: -18.0000,
          lng: 31.5000,
          activity_type: 'Document Forgery',
          confidence: 0.91,
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          description: 'Tampered document - hologram verification failed',
          user_info: { full_name: 'Emily Davis', username: 'edavis' },
          document_info: { type: 'National ID', number: 'ZW147258' }
        },
        {
          id: 7,
          lat: -19.0000,
          lng: 32.5000,
          activity_type: 'Biometric Mismatch',
          confidence: 0.85,
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          description: 'Facial features do not match document photo',
          user_info: { full_name: 'Robert Wilson', username: 'rwilson' },
          document_info: { type: 'Passport', number: 'ZW369258' }
        }
      ]
      setMapData(demoMapData)
    } finally {
      setLoading(false)
    }
  }

  const loadHotspots = async () => {
    try {
      const response = await mapAPI.getHotspots()
      setHotspots(response.data.hotspots)
    } catch (error) {
      console.error('Error loading hotspots:', error)
      // Use demo hotspot data when backend is not available
      const demoHotspots = [
        {
          lat: -17.8252,
          lng: 31.0335,
          activity_count: 15,
          avg_confidence: 0.88,
          risk_level: 'High',
          location_name: 'Harare Central'
        },
        {
          lat: -20.1393,
          lng: 28.5833,
          activity_count: 8,
          avg_confidence: 0.75,
          risk_level: 'Medium',
          location_name: 'Bulawayo'
        },
        {
          lat: -18.9645,
          lng: 32.7700,
          activity_count: 12,
          avg_confidence: 0.82,
          risk_level: 'High',
          location_name: 'Harare'
        }
      ]
      setHotspots(demoHotspots)
    }
  }

  const loadStatistics = async () => {
    try {
      const response = await mapAPI.getStatistics()
      setStatistics(response.data.statistics)
    } catch (error) {
      console.error('Error loading statistics:', error)
      // Use demo statistics when backend is not available
      const demoStatistics = {
        activity_stats: {
          total: 7,
          high_confidence: 5,
          last_24h: 2
        },
        region_stats: {
          'Harare': 3,
          'Bulawayo': 1,
          'Gweru': 1,
          'Mutare': 2
        }
      }
      setStatistics(demoStatistics)
    }
  }

  const loadAdvancedAnalytics = async () => {
    try {
      // In production, these would be separate API calls
      // For now, using demo data
      
      // Trending Manipulation Types
      setTrendingManipulations([
        { type: 'Deepfake Face Swap', count: 45, change: '+23%', trend: 'up', severity: 'critical' },
        { type: 'AI-Generated Documents', count: 38, change: '+15%', trend: 'up', severity: 'high' },
        { type: 'Voice Cloning', count: 29, change: '+8%', trend: 'up', severity: 'high' },
        { type: 'Photo Manipulation', count: 22, change: '-5%', trend: 'down', severity: 'medium' },
        { type: 'Synthetic ID Creation', count: 18, change: '+12%', trend: 'up', severity: 'high' }
      ])

      // High Risk Users
      setHighRiskUsers([
        { username: 'user_2847', riskScore: 94, incidents: 12, lastActivity: '2 hours ago', location: 'Harare' },
        { username: 'user_5923', riskScore: 89, incidents: 9, lastActivity: '5 hours ago', location: 'Bulawayo' },
        { username: 'user_1034', riskScore: 85, incidents: 8, lastActivity: '1 day ago', location: 'Harare' },
        { username: 'user_7621', riskScore: 82, incidents: 7, lastActivity: '3 hours ago', location: 'Gweru' },
        { username: 'user_4389', riskScore: 78, incidents: 6, lastActivity: '12 hours ago', location: 'Mutare' }
      ])

      // Spike Alerts
      setSpikeAlerts([
        {
          id: 1,
          title: 'Coordinated Disinformation Attack',
          description: '350% increase in AI-generated fake documents in last 6 hours',
          severity: 'critical',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          affectedRegions: ['Harare', 'Chitungwiza'],
          count: 87
        },
        {
          id: 2,
          title: 'Deepfake Surge Detected',
          description: 'Unusual spike in face-swap deepfakes targeting government IDs',
          severity: 'high',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          affectedRegions: ['Bulawayo'],
          count: 45
        },
        {
          id: 3,
          title: 'Bot Network Activity',
          description: 'Multiple automated verification attempts from coordinated sources',
          severity: 'high',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          affectedRegions: ['Gweru', 'Kwekwe'],
          count: 62
        }
      ])

      // AI Misinformation Hotspots
      setAiMisinfoHotspots([
        { lat: -17.8252, lng: 31.0335, intensity: 92, name: 'Harare Central', incidents: 127 },
        { lat: -20.1393, lng: 28.5833, intensity: 78, name: 'Bulawayo', incidents: 89 },
        { lat: -18.9645, lng: 32.7700, intensity: 85, name: 'Harare East', incidents: 103 }
      ])

      // Suspicious Activity Clusters
      setSuspiciousClusters([
        {
          id: 1,
          center: { lat: -17.8252, lng: 31.0335 },
          radius: 5000,
          activityCount: 34,
          pattern: 'Multiple users, same IP range, similar timestamps',
          confidence: 0.91
        },
        {
          id: 2,
          center: { lat: -20.1393, lng: 28.5833 },
          radius: 3500,
          activityCount: 23,
          pattern: 'Repeated document templates with minor variations',
          confidence: 0.87
        }
      ])

      // Deepfake Activity by Region
      setDeepfakeRegions([
        { region: 'Harare', current: 45, previous: 32, change: '+40.6%', trend: 'rising' },
        { region: 'Bulawayo', current: 28, previous: 24, change: '+16.7%', trend: 'rising' },
        { region: 'Gweru', current: 15, previous: 18, change: '-16.7%', trend: 'falling' },
        { region: 'Mutare', current: 22, previous: 14, change: '+57.1%', trend: 'rising' },
        { region: 'Chitungwiza', current: 31, previous: 19, change: '+63.2%', trend: 'rising' }
      ])
    } catch (error) {
      console.error('Error loading advanced analytics:', error)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleRefresh = () => {
    loadMapData()
    loadStatistics()
    if (activeView === 'hotspots') {
      loadHotspots()
    }
  }

  const handleExport = async (format = 'json') => {
    try {
      const response = await mapAPI.exportData(format)
      
      if (format === 'csv') {
        // Create and download CSV file
        const blob = new Blob([response.data.csv_data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `suspicious_activities_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(response.data.export_data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `suspicious_activities_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
      
      toast.success(`Data exported as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Export failed')
    }
  }

  const getMarkerIcon = (activity) => {
    const confidence = activity.confidence || 0
    let color = '#dc2626' // red - default for flagged documents
    
    // All flagged documents should show red pins
    if (confidence >= 0.7) {
      color = '#dc2626' // red - flagged/suspicious
    } else if (confidence >= 0.5) {
      color = '#f59e0b' // orange - moderate concern
    } else {
      color = '#6b7280' // gray - low concern
    }

    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    })
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-green-400 font-mono">
            <span className="text-blue-400">[</span>Geo Security Map<span className="text-blue-400">]</span>
          </h1>
          <p className="mt-2 text-gray-300 font-mono">
            <span className="text-green-500">#</span> Real-time suspicious activity tracking across Zimbabwe
          </p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-red-400/50 transition-all duration-300">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <div className="ml-4">
                  <p className="text-2xl font-mono text-red-400">
                    {statistics.activity_stats.total || 0}
                  </p>
                  <p className="text-gray-400 font-mono">Total Activities</p>
                </div>
              </div>
            </div>

            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-yellow-400/50 transition-all duration-300">
              <div className="flex items-center">
                <Eye className="h-8 w-8 text-yellow-400" />
                <div className="ml-4">
                  <p className="text-2xl font-mono text-yellow-400">
                    {statistics.activity_stats.high_confidence || 0}
                  </p>
                  <p className="text-gray-400 font-mono">High Risk</p>
                </div>
              </div>
            </div>

            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-blue-400/50 transition-all duration-300">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 text-blue-400" />
                <div className="ml-4">
                  <p className="text-2xl font-mono text-blue-400">
                    {statistics.activity_stats.last_24h || 0}
                  </p>
                  <p className="text-gray-400 font-mono">Last 24h</p>
                </div>
              </div>
            </div>

            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-green-400" />
                <div className="ml-4">
                  <p className="text-2xl font-mono text-green-400">
                    {Object.keys(statistics.region_stats || {}).length}
                  </p>
                  <p className="text-gray-400 font-mono">Active Regions</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* View Toggle */}
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveView('activities')}
                className={`px-4 py-2 rounded-lg font-mono transition-all duration-300 ${
                  activeView === 'activities'
                    ? 'bg-green-600/40 text-green-400 border border-green-500/50'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 border border-gray-600/30'
                }`}
              >
                [Activities]
              </button>
              <button
                onClick={() => {
                  setActiveView('hotspots')
                  loadHotspots()
                }}
                className={`px-4 py-2 rounded-lg font-mono transition-all duration-300 ${
                  activeView === 'hotspots'
                    ? 'bg-green-600/40 text-green-400 border border-green-500/50'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 border border-gray-600/30'
                }`}
              >
                [Hotspots]
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={filters.activity_type}
                  onChange={(e) => handleFilterChange('activity_type', e.target.value)}
                  className="bg-gray-800/50 border border-gray-600/30 text-gray-300 font-mono text-sm rounded-md px-3 py-2 focus:outline-none focus:border-green-500/50"
                >
                  <option value="">All Types</option>
                  <option value="low_confidence_verification">Low Confidence</option>
                  <option value="document_forgery">Document Forgery</option>
                  <option value="biometric_mismatch">Biometric Mismatch</option>
                  <option value="suspicious_pattern">Suspicious Pattern</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400 font-mono">Confidence:</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={filters.confidence_threshold}
                  onChange={(e) => handleFilterChange('confidence_threshold', parseFloat(e.target.value))}
                  className="w-20 accent-green-400"
                />
                <span className="text-sm font-mono text-green-400">
                  {formatConfidence(filters.confidence_threshold)}
                </span>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={handleRefresh}
                  className="bg-gray-800/50 border border-gray-600/30 text-gray-300 hover:bg-gray-700/50 hover:border-green-500/50 px-4 py-2 rounded-md font-mono text-sm transition-all duration-300 flex items-center space-x-2"
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>[Refresh]</span>
                </button>

                <div className="relative group">
                  <button className="bg-gray-800/50 border border-gray-600/30 text-gray-300 hover:bg-gray-700/50 hover:border-blue-500/50 px-4 py-2 rounded-md font-mono text-sm transition-all duration-300 flex items-center space-x-2">
                    <Download className="h-4 w-4" />
                    <span>[Export]</span>
                  </button>
                  <div className="absolute right-0 mt-2 w-32 bg-black/95 backdrop-blur-md border border-green-500/30 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => handleExport('json')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-green-400 hover:bg-green-500/10 font-mono"
                      >
                        JSON
                      </button>
                      <button
                        onClick={() => handleExport('csv')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-green-400 hover:bg-green-500/10 font-mono"
                      >
                        CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-0 overflow-hidden">
          <div style={{ height: '600px' }}>
            {loading ? (
              <div className="flex items-center justify-center h-full bg-black">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-400"></div>
              </div>
            ) : (
              <MapContainer
                center={[-18.9645, 32.7700]} // Zimbabwe center (Harare)
                zoom={6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {activeView === 'activities' && mapData.map((activity) => (
                  <Marker
                    key={activity.id}
                    position={[activity.lat, activity.lng]}
                    icon={getMarkerIcon(activity)}
                    eventHandlers={{
                      click: () => setSelectedActivity(activity)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Suspicious Activity #{activity.id}
                        </h3>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="font-medium">Type:</span> {activity.activity_type}
                          </div>
                          <div>
                            <span className="font-medium">Confidence:</span> {formatConfidence(activity.confidence)}
                          </div>
                          <div>
                            <span className="font-medium">User:</span> {activity.user_info?.full_name || 'Unknown'}
                          </div>
                          <div>
                            <span className="font-medium">Time:</span> {formatDate(activity.timestamp)}
                          </div>
                          <div>
                            <span className="font-medium">Location:</span> {activity.lat.toFixed(4)}, {activity.lng.toFixed(4)}
                          </div>
                        </div>
                        {activity.description && (
                          <div className="mt-2 text-sm text-gray-600">
                            {activity.description}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {activeView === 'hotspots' && hotspots.map((hotspot, index) => (
                  <Circle
                    key={index}
                    center={[hotspot.lat, hotspot.lng]}
                    radius={hotspot.activity_count * 1000} // Radius based on activity count
                    pathOptions={{
                      color: hotspot.avg_confidence > 0.8 ? '#dc2626' : hotspot.avg_confidence > 0.6 ? '#f59e0b' : '#10b981',
                      fillColor: hotspot.avg_confidence > 0.8 ? '#dc2626' : hotspot.avg_confidence > 0.6 ? '#f59e0b' : '#10b981',
                      fillOpacity: 0.3
                    }}
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-semibold text-gray-900 mb-2">Activity Hotspot</h3>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="font-medium">Activities:</span> {hotspot.activity_count}
                          </div>
                          <div>
                            <span className="font-medium">Avg Confidence:</span> {formatConfidence(hotspot.avg_confidence)}
                          </div>
                          <div>
                            <span className="font-medium">Risk Level:</span> 
                            <span className={`ml-1 px-2 py-1 rounded text-xs ${getStatusColor(hotspot.risk_level)}`}>
                              {hotspot.risk_level}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Location:</span> {hotspot.location_name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Circle>
                ))}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 mt-6">
          <h3 className="text-lg font-mono text-green-400 mb-4">Map Legend</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-full bg-red-500 border-2 border-white"></div>
              <span className="text-sm text-gray-300 font-mono">Flagged / High Risk (70%+)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-full bg-orange-500 border-2 border-white"></div>
              <span className="text-sm text-gray-300 font-mono">Moderate Concern (50-70%)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-full bg-gray-500 border-2 border-white"></div>
              <span className="text-sm text-gray-300 font-mono">Low Concern (&lt;50%)</span>
            </div>
          </div>
        </div>

        {/* Advanced Analytics Toggle */}
        <div className="mt-6">
          <button
            onClick={() => setShowAdvancedAnalytics(!showAdvancedAnalytics)}
            className="w-full bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 hover:border-purple-400/50 text-purple-400 hover:text-purple-300 py-3 px-6 rounded-lg font-mono transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Shield className="h-5 w-5" />
            <span>{showAdvancedAnalytics ? '[-] Hide' : '[+] Show'} Advanced AI Security Analytics</span>
          </button>
        </div>

        {/* Advanced Analytics Sections */}
        {showAdvancedAnalytics && (
          <div className="mt-6 space-y-6">
            {/* Spike Alerts */}
            <div className="bg-black/90 backdrop-blur-md border border-red-500/30 rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Zap className="h-6 w-6 text-red-400" />
                <h3 className="text-lg font-mono text-red-400">
                  <span className="text-blue-400">[</span>Critical Spike Alerts<span className="text-blue-400">]</span>
                </h3>
              </div>
              <div className="space-y-3">
                {spikeAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-4 border rounded-lg ${
                      alert.severity === 'critical'
                        ? 'border-red-500/50 bg-red-500/10'
                        : 'border-orange-500/50 bg-orange-500/10'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-mono text-white font-semibold">{alert.title}</h4>
                        <p className="text-sm text-gray-300 font-mono mt-1">{alert.description}</p>
                      </div>
                      <span
                        className={`ml-4 px-3 py-1 rounded-full text-xs font-mono ${
                          alert.severity === 'critical'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                            : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        }`}
                      >
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm font-mono text-gray-400 mt-2">
                      <span>📊 {alert.count} incidents</span>
                      <span>📍 {alert.affectedRegions.join(', ')}</span>
                      <span>🕐 {formatDate(alert.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trending Manipulations & High Risk Users Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trending Manipulation Types */}
              <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <TrendingUp className="h-6 w-6 text-orange-400" />
                  <h3 className="text-lg font-mono text-orange-400">
                    <span className="text-blue-400">[</span>Trending Manipulations<span className="text-blue-400">]</span>
                  </h3>
                </div>
                <div className="space-y-3">
                  {trendingManipulations.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 border border-green-500/20 rounded-lg hover:bg-green-500/5 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-gray-300">{item.type}</span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-mono ${
                              item.severity === 'critical'
                                ? 'bg-red-500/20 text-red-400'
                                : item.severity === 'high'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}
                          >
                            {item.severity}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3 mt-1 text-sm">
                          <span className="text-gray-400 font-mono">{item.count} cases</span>
                          <span
                            className={`font-mono ${
                              item.trend === 'up' ? 'text-red-400' : 'text-green-400'
                            }`}
                          >
                            {item.change} {item.trend === 'up' ? '↑' : '↓'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* High Risk Users */}
              <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="h-6 w-6 text-red-400" />
                  <h3 className="text-lg font-mono text-red-400">
                    <span className="text-blue-400">[</span>High Risk Users<span className="text-blue-400">]</span>
                  </h3>
                </div>
                <div className="space-y-3">
                  {highRiskUsers.map((user, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 border border-red-500/20 rounded-lg hover:bg-red-500/5 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-gray-300">{user.username}</span>
                          <span className="text-xs text-gray-500 font-mono">📍 {user.location}</span>
                        </div>
                        <div className="flex items-center space-x-3 mt-1 text-sm font-mono">
                          <span className="text-gray-400">{user.incidents} incidents</span>
                          <span className="text-gray-500">• {user.lastActivity}</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center font-mono font-bold ${
                            user.riskScore >= 90
                              ? 'bg-red-500/20 text-red-400 border-2 border-red-500'
                              : user.riskScore >= 80
                              ? 'bg-orange-500/20 text-orange-400 border-2 border-orange-500'
                              : 'bg-yellow-500/20 text-yellow-400 border-2 border-yellow-500'
                          }`}
                        >
                          {user.riskScore}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Deepfake Regions */}
            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Activity className="h-6 w-6 text-purple-400" />
                <h3 className="text-lg font-mono text-purple-400">
                  <span className="text-blue-400">[</span>Deepfake Activity by Region<span className="text-blue-400">]</span>
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {deepfakeRegions.map((region, idx) => (
                  <div
                    key={idx}
                    className={`p-4 border rounded-lg ${
                      region.trend === 'rising'
                        ? 'border-red-500/30 bg-red-500/5'
                        : 'border-green-500/30 bg-green-500/5'
                    }`}
                  >
                    <div className="text-center">
                      <h4 className="font-mono text-white font-semibold mb-2">{region.region}</h4>
                      <div className="text-3xl font-mono text-white mb-1">{region.current}</div>
                      <div className="text-xs text-gray-400 font-mono mb-2">
                        Previous: {region.previous}
                      </div>
                      <div
                        className={`text-sm font-mono font-semibold ${
                          region.trend === 'rising' ? 'text-red-400' : 'text-green-400'
                        }`}
                      >
                        {region.change} {region.trend === 'rising' ? '📈' : '📉'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Misinformation Hotspots */}
            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-yellow-400" />
                <h3 className="text-lg font-mono text-yellow-400">
                  <span className="text-blue-400">[</span>AI Misinformation Hotspots<span className="text-blue-400">]</span>
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {aiMisinfoHotspots.map((hotspot, idx) => (
                  <div
                    key={idx}
                    className="p-4 border border-yellow-500/30 rounded-lg bg-yellow-500/5"
                  >
                    <div className="text-center">
                      <h4 className="font-mono text-white font-semibold mb-2">{hotspot.name}</h4>
                      <div className="mb-3">
                        <div className="text-2xl font-mono text-yellow-400 mb-1">
                          {hotspot.intensity}%
                        </div>
                        <div className="text-xs text-gray-400 font-mono">Intensity Score</div>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                        <div
                          className="bg-gradient-to-r from-yellow-500 to-red-500 h-2 rounded-full"
                          style={{ width: `${hotspot.intensity}%` }}
                        ></div>
                      </div>
                      <div className="text-sm font-mono text-gray-300">
                        {hotspot.incidents} incidents
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Suspicious Activity Clusters */}
            <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Eye className="h-6 w-6 text-cyan-400" />
                <h3 className="text-lg font-mono text-cyan-400">
                  <span className="text-blue-400">[</span>Suspicious Activity Clusters<span className="text-blue-400">]</span>
                </h3>
              </div>
              <div className="space-y-4">
                {suspiciousClusters.map((cluster) => (
                  <div
                    key={cluster.id}
                    className="p-4 border border-cyan-500/30 rounded-lg bg-cyan-500/5"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-mono text-white font-semibold mb-1">
                          Cluster #{cluster.id}
                        </h4>
                        <p className="text-sm text-gray-300 font-mono">{cluster.pattern}</p>
                      </div>
                      <span className="ml-4 px-3 py-1 rounded-full text-xs font-mono bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                        {formatConfidence(cluster.confidence)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm font-mono text-gray-400 mt-2">
                      <span>📊 {cluster.activityCount} activities</span>
                      <span>📍 Radius: {(cluster.radius / 1000).toFixed(1)}km</span>
                      <span>
                        🎯 {cluster.center.lat.toFixed(4)}, {cluster.center.lng.toFixed(4)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reality Reconstruction Section */}
            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <ImageIcon className="h-6 w-6 text-purple-400" />
                <h3 className="text-lg font-mono text-purple-400">
                  <span className="text-blue-400">[</span>Reality Reconstruction Analysis<span className="text-blue-400">]</span>
                </h3>
              </div>
              <div className="space-y-4">
                <p className="text-gray-300 font-mono text-sm">
                  Advanced AI-powered image analysis to detect manipulation and reconstruct original content
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 border border-purple-500/20 rounded-lg bg-purple-500/5">
                    <div className="text-center">
                      <Search className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <h4 className="font-mono text-white text-sm mb-1">Background Reconstruction</h4>
                      <p className="text-xs text-gray-400 font-mono">Rebuild removed or altered backgrounds</p>
                    </div>
                  </div>
                  <div className="p-4 border border-purple-500/20 rounded-lg bg-purple-500/5">
                    <div className="text-center">
                      <Activity className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <h4 className="font-mono text-white text-sm mb-1">Reverse Diffusion</h4>
                      <p className="text-xs text-gray-400 font-mono">Trace AI generation patterns</p>
                    </div>
                  </div>
                  <div className="p-4 border border-purple-500/20 rounded-lg bg-purple-500/5">
                    <div className="text-center">
                      <Filter className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <h4 className="font-mono text-white text-sm mb-1">Artifact Detection</h4>
                      <p className="text-xs text-gray-400 font-mono">Reveal hidden AI manipulation markers</p>
                    </div>
                  </div>
                  <div className="p-4 border border-purple-500/20 rounded-lg bg-purple-500/5">
                    <div className="text-center">
                      <BarChart3 className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <h4 className="font-mono text-white text-sm mb-1">Database Comparison</h4>
                      <p className="text-xs text-gray-400 font-mono">Match against real image databases</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 border border-green-500/30 rounded-lg bg-green-500/5">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-mono text-green-400 font-semibold mb-1">Analysis Capabilities</h4>
                      <ul className="text-sm text-gray-300 font-mono space-y-1">
                        <li>• Detect GAN-generated synthetic faces</li>
                        <li>• Identify deepfake video manipulation frames</li>
                        <li>• Reconstruct tampered document sections</li>
                        <li>• Compare against verified identity databases</li>
                        <li>• Extract metadata and generation signatures</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Details Modal */}
        {selectedActivity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Activity Details
                </h3>
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <span className="font-medium text-gray-700">Activity ID:</span>
                  <span className="ml-2 text-gray-900">#{selectedActivity.id}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <span className="ml-2 text-gray-900">{selectedActivity.activity_type}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Confidence Score:</span>
                  <span className={`ml-2 font-medium ${getConfidenceColor(selectedActivity.confidence)}`}>
                    {formatConfidence(selectedActivity.confidence)}
                  </span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">User:</span>
                  <span className="ml-2 text-gray-900">
                    {selectedActivity.user_info?.full_name || 'Unknown'} 
                    ({selectedActivity.user_info?.username || 'N/A'})
                  </span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Document:</span>
                  <span className="ml-2 text-gray-900">
                    {selectedActivity.document_info?.type || 'Unknown'} 
                    {selectedActivity.document_info?.number && ` - ${selectedActivity.document_info.number}`}
                  </span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Timestamp:</span>
                  <span className="ml-2 text-gray-900">{formatDate(selectedActivity.timestamp)}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="ml-2 text-gray-900">
                    {selectedActivity.lat.toFixed(6)}, {selectedActivity.lng.toFixed(6)}
                  </span>
                </div>
                
                {selectedActivity.description && (
                  <div>
                    <span className="font-medium text-gray-700">Description:</span>
                    <p className="mt-1 text-gray-900 text-sm">{selectedActivity.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GeoMap